import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SectionHead, Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const AZURE = {
  title: "Azure Expertise",
  tagline: "Deep expertise in Azure's comprehensive platform",
  logos: ["App Service", "AKS", "Azure SQL", "CosmosDB", "Azure DevOps"],
  items: ["Cloud infrastructure management", "PaaS solutions for rapid deployment", "Managed databases and data services", "CI/CD automation with Azure Pipelines"],
};

const AWS = {
  title: "AWS Expertise",
  tagline: "Certified AWS solutions for enterprise workloads",
  logos: ["EC2", "RDS", "S3", "Lambda", "ECS", "EKS"],
  items: ["Compute and container orchestration", "Database and storage solutions", "Serverless computing", "Network and security services"],
};

const EXTRA = ["Docker", "Kubernetes", "Terraform", "Git", "Jenkins", "Linux", "SQL / NoSQL", "DataDog", "New Relic"];

function Pillar({ data, accent, testId }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-nexblue/10 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]" data-testid={testId}>
      <h3 className="font-display text-xl sm:text-2xl font-semibold text-nexblue">{data.title}</h3>
      <p className="mt-2 text-sm text-nexink/60">{data.tagline}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {data.logos.map((l) => (
          <span key={l} className={`chip-glow rounded-full border px-3.5 py-1.5 text-xs font-semibold ${accent === "cyan" ? "border-nexcyan/30 bg-nexcyan/5 text-nexblue" : "border-nexorange/30 bg-nexorange/5 text-nexblue"}`}>
            {l}
          </span>
        ))}
      </div>
      <ul className="mt-6 space-y-2.5">
        {data.items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-sm text-nexink/70">
            <span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${accent === "cyan" ? "bg-nexcyan" : "bg-nexorange"}`} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TechStack() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="expertise" ref={ref} data-testid="expertise-section" className="py-24 sm:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          chapter="03"
          overline="Technology Stack"
          title="Our Technology Stack"
          sub="Certified depth on the platforms that run the world's most demanding workloads."
          testId="expertise-heading"
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Stagger className="grid grid-cols-1 gap-8">
            <StaggerItem><Pillar data={AZURE} accent="cyan" testId="azure-pillar" /></StaggerItem>
            <StaggerItem><Pillar data={AWS} accent="orange" testId="aws-pillar" /></StaggerItem>
          </Stagger>

          <Reveal delay={0.15} className="relative">
            <div className="relative h-full min-h-[420px] overflow-hidden rounded-2xl">
              <motion.img
                style={{ y: yImg }}
                src="https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjbG91ZCUyMGNvbXB1dGluZyUyMHNlcnZlciUyMGRhdGFjZW50ZXJ8ZW58MHx8fHwxNzg4MzY1MDczfDA&ixlib=rb-4.1.0&q=85"
                alt="Cloud datacenter server racks"
                loading="lazy"
                className="absolute inset-0 h-[115%] w-full object-cover"
                data-testid="expertise-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-nexblue via-nexblue/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">Always-On Operations</p>
                <p className="mt-3 max-w-sm font-display text-xl font-semibold text-white">
                  Infrastructure we monitor, optimize, and stand behind — around the clock.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="rounded-2xl border border-nexblue/10 bg-nexlight px-8 py-7" data-testid="additional-tech-strip">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-nexink/50 mb-4">Additional Technologies</p>
            <div className="flex flex-wrap gap-2.5">
              {EXTRA.map((t) => (
                <span key={t} className="chip-glow rounded-full border border-nexblue/15 bg-white px-4 py-2 text-xs font-semibold text-nexink/80">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
